import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DeviceDTO, PairingCodeDTO } from "../../api/contract";
import {
  ApiRequestError,
  createPairing,
  deletePairing,
  getExtensionVersion,
  listDevices,
  listDeviceTasks,
  listPairings,
  renameDevice,
  revokeDevice,
  type DeviceTaskItem,
} from "../api";
import { DeviceDetailDrawer } from "./extension/DeviceDetailDrawer";
import { DevicesCard } from "./extension/DevicesCard";
import { ExtensionInstall } from "./extension/ExtensionInstall";
import { PairingModal, type PairingState } from "./extension/PairingModal";

type ToastFn = (kind: "ok" | "err", msg: string) => void;

export function Extension({ pushToast, openPairing = false }: { pushToast: ToastFn; openPairing?: boolean }) {
  const [devices, setDevices] = useState<DeviceDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [pairOpen, setPairOpen] = useState(openPairing);
  const [pairLabel, setPairLabel] = useState("");
  const [pairBusy, setPairBusy] = useState(false);
  const [pairing, setPairing] = useState<PairingState | null>(null);

  const [activePairings, setActivePairings] = useState<PairingCodeDTO[]>([]);
  const lastPairingRef = useRef<PairingCodeDTO | null>(null);
  const pairBusyRef = useRef(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [versionErr, setVersionErr] = useState(false);
  const [updateGuideOpen, setUpdateGuideOpen] = useState(false);
  const installGuideRef = useRef<HTMLDetailsElement>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<DeviceTaskItem[]>([]);
  const [selectedTasksErr, setSelectedTasksErr] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [revokeBusy, setRevokeBusy] = useState(false);

  const reloadAll = useCallback(async () => {
    setLoadErr(null);
    try {
      const [dev, pair] = await Promise.all([listDevices(), listPairings()]);
      setDevices(dev);
      setActivePairings(pair);
      // Tieni solo id ancora attivi (non revocati) nella selezione multipla.
      const active = new Set(dev.filter((d) => !d.revokedAt).map((d) => d.id));
      setSelectedIds((prev) => {
        const next = new Set<string>();
        for (const id of prev) {
          if (active.has(id)) next.add(id);
        }
        return next.size === prev.size ? prev : next;
      });
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : "Errore caricamento";
      setLoadErr(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadAll();
    const id = setInterval(() => void reloadAll(), 15_000);
    return () => clearInterval(id);
  }, [reloadAll]);

  useEffect(() => {
    let cancelled = false;
    getExtensionVersion()
      .then(({ version }) => { if (!cancelled) setCurrentVersion(version); })
      .catch(() => { if (!cancelled) setVersionErr(true); });
    return () => { cancelled = true; };
  }, []);

  // Pairing countdown
  const pairingRef = useRef(pairing);
  pairingRef.current = pairing;
  useEffect(() => {
    if (!pairing) return;
    const handle = setInterval(() => {
      const cur = pairingRef.current;
      if (!cur) return;
      const expires = new Date(cur.pairing.expiresAt).getTime();
      const remaining = Math.max(0, expires - Date.now());
      setPairing({ pairing: cur.pairing, remainingMs: remaining });
      if (remaining === 0) clearInterval(handle);
    }, 1000);
    return () => clearInterval(handle);
  }, [pairing?.pairing.id]);

  const generatePairing = async () => {
    if (pairBusyRef.current) return;
    pairBusyRef.current = true;
    setPairBusy(true);
    try {
      const previous = lastPairingRef.current;
      if (previous && new Date(previous.expiresAt).getTime() > Date.now()) {
        try {
          await deletePairing(previous.id);
        } catch (err) {
          if (!(err instanceof ApiRequestError && err.status === 404)) throw err;
        }
        lastPairingRef.current = null;
      }
      const created = await createPairing({ label: pairLabel.trim() || undefined });
      lastPairingRef.current = created;
      setPairing({ pairing: created, remainingMs: Math.max(0, new Date(created.expiresAt).getTime() - Date.now()) });
      pushToast("ok", `Codice ${created.code} generato`);
      void reloadAll();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : "Errore generazione codice";
      pushToast("err", msg);
    } finally {
      pairBusyRef.current = false;
      setPairBusy(false);
    }
  };

  const cancelPairing = async () => {
    if (pairBusyRef.current) return;
    const current = pairingRef.current;
    if (!current) {
      setPairOpen(false);
      return;
    }
    pairBusyRef.current = true;
    setPairBusy(true);
    try {
      try {
        await deletePairing(current.pairing.id);
      } catch (err) {
        if (!(err instanceof ApiRequestError && err.status === 404)) throw err;
      }
      lastPairingRef.current = null;
      setPairing(null);
      setPairOpen(false);
      pushToast("ok", `Codice ${current.pairing.code} annullato`);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : "Errore annullamento";
      pushToast("err", msg);
    } finally {
      pairBusyRef.current = false;
      setPairBusy(false);
      void reloadAll();
    }
  };

  const openDeviceDetail = (id: string) => {
    setSelectedId(id);
    const dev = devices.find((d) => d.id === id);
    setRenameValue(dev?.label ?? "");
    setSelectedTasks([]);
    setSelectedTasksErr(null);
  };

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    listDeviceTasks(selectedId, 30)
      .then((res) => {
        if (!cancelled) setSelectedTasks(res.items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof ApiRequestError ? err.message : "Errore task";
        setSelectedTasksErr(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const submitRename = async () => {
    if (!selectedId) return;
    const newLabel = renameValue.trim();
    if (!newLabel) return;
    try {
      const updated = await renameDevice(selectedId, newLabel);
      setDevices((prev) => prev.map((d) => (d.id === selectedId ? updated : d)));
      pushToast("ok", "Nome aggiornato");
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : "Errore rinomina";
      pushToast("err", msg);
    }
  };

  const markRevoked = (ids: string[]) => {
    const now = new Date().toISOString();
    const idSet = new Set(ids);
    setDevices((prev) =>
      prev.map((d) => (idSet.has(d.id) ? { ...d, revokedAt: now, online: false } : d)),
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
    if (selectedId && idSet.has(selectedId)) setSelectedId(null);
  };

  const doRevoke = async (id: string) => {
    if (!confirm("Revocare questa estensione? Il device non potrà più connettersi.")) return;
    try {
      await revokeDevice(id);
      markRevoked([id]);
      pushToast("ok", "Estensione revocata");
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : "Errore revoca";
      pushToast("err", msg);
    }
  };

  const doBulkRevoke = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!confirm(`Revocare ${ids.length} estensioni? Non potranno più connettersi.`)) return;
    setRevokeBusy(true);
    let ok = 0;
    let failed = 0;
    try {
      for (const id of ids) {
        try {
          await revokeDevice(id);
          ok += 1;
          markRevoked([id]);
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) pushToast("ok", `${ok} estensioni revocate`);
      else pushToast("err", `Revocate ${ok}, non riuscite ${failed}`);
    } finally {
      setRevokeBusy(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visible = devices.filter((d) => !d.revokedAt);
    setSelectedIds((prev) => {
      const allSelected = visible.length > 0 && visible.every((d) => prev.has(d.id));
      if (allSelected) return new Set();
      return new Set(visible.map((d) => d.id));
    });
  };

  const counts = useMemo(() => {
    const online = devices.filter((d) => d.online && !d.revokedAt).length;
    const offline = devices.filter((d) => !d.online && !d.revokedAt).length;
    const revoked = devices.filter((d) => d.revokedAt).length;
    return { online, offline, revoked, total: devices.length };
  }, [devices]);

  const selected = selectedId ? devices.find((d) => d.id === selectedId) : null;
  const guideFirst = !loading && counts.online + counts.offline === 0;
  const openPairingModal = () => {
    if (pairBusyRef.current) return;
    setPairOpen(true); setPairing(null); setPairLabel("");
  };
  const installGuide = (
    <details className="card install-guide" open={guideFirst} ref={installGuideRef}>
      <summary>
        <span>Installa o aggiorna l'estensione</span>
        <span className="muted">guida passo per passo</span>
      </summary>
      <div className="card-pad">
        <ExtensionInstall onOpenPairing={openPairingModal} version={currentVersion} versionErr={versionErr} updateGuideOpen={updateGuideOpen} />
      </div>
    </details>
  );

  return (
    <div className="col gap-16">
      {guideFirst && installGuide}
      <DevicesCard
        currentVersion={currentVersion}
        activePairings={activePairings}
        counts={counts}
        devices={devices}
        loading={loading}
        loadErr={loadErr}
        selectedIds={selectedIds}
        revokeBusy={revokeBusy}
        onNewPairing={openPairingModal}
        onOpenDevice={openDeviceDetail}
        onRevoke={(id) => void doRevoke(id)}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onBulkRevoke={() => void doBulkRevoke()}
        onClearSelection={() => setSelectedIds(new Set())}
        onOpenUpdateGuide={() => {
          if (installGuideRef.current) installGuideRef.current.open = true;
          const instructions = installGuideRef.current?.querySelector<HTMLDetailsElement>("#aggiornare-estensione");
          if (instructions) instructions.open = true;
          setUpdateGuideOpen(true);
        }}
      />

      {!guideFirst && installGuide}

      <PairingModal
        open={pairOpen}
        pairing={pairing}
        pairLabel={pairLabel}
        pairBusy={pairBusy}
        onClose={() => { if (!pairBusyRef.current && !pairingRef.current) setPairOpen(false); }}
        onCancelPairing={() => void cancelPairing()}
        onClosePairing={() => {
          if (pairBusyRef.current) return;
          setPairing(null); setPairOpen(false); void reloadAll();
        }}
        onGeneratePairing={() => void generatePairing()}
        onLabelChange={setPairLabel}
      />

      <DeviceDetailDrawer
        selected={selected}
        selectedId={selectedId}
        selectedTasks={selectedTasks}
        selectedTasksErr={selectedTasksErr}
        renameValue={renameValue}
        onClose={() => setSelectedId(null)}
        onRenameChange={setRenameValue}
        onSubmitRename={() => void submitRename()}
        onRevoke={(id) => void doRevoke(id)}
      />
    </div>
  );
}
